import { useCallback, useEffect, useState } from "react";
import { hivesApi } from "../../services/hivesApi";
import { queensApi } from "../../services/queensApi";
import { useToast } from "../../context/ToastContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { EmptyState } from "../../components/common/EmptyState";
import { Spinner } from "../../components/common/Spinner";

/**
 * Why a hive is requeened, in the words a beekeeper would use (#259).
 *
 * The values are the five the CHECK constraint in V056 permits; the labels
 * are what actually happened. "Failed" on its own is ambiguous enough to
 * be picked wrongly -- a failing queen and a dead queen are different
 * events, and telling them apart later is the entire point of storing a
 * reason at all.
 */
const REASONS = [
  { value: "Failed", label: "She was failing — poor brood pattern, drone layer" },
  { value: "Died", label: "She died or was lost" },
  { value: "Superseded", label: "The colony superseded her themselves" },
  { value: "Swarmed", label: "The colony swarmed and raised a new one" },
  { value: "Planned", label: "Planned requeening — genetics, temperament, age" },
];

const EMPTY_QUEEN = { source: "", birth_year: "", marking_color: "", genetic_line: "" };

function describeQueen(marking, line, year) {
  return [marking || "unmarked", line || "line unknown", year ? `b. ${year}` : null]
    .filter(Boolean)
    .join(", ");
}

export function QueenTab({ hive, onChanged }) {
  const { notify } = useToast();

  /*
   * The lineage box spun forever (#259).
   *
   * It was `hivesApi.queenLineage(id).then((r) => setLineage(r.lineage))`
   * with no catch and no error state, and the render was
   * `!lineage ? <Spinner/>`. So EVERY failure -- a 403 on a tier without
   * QueenManagement, a 500, a dropped connection -- left `lineage` null
   * forever and the user watching an animation that would never resolve.
   * A spinner is a promise that something is coming; when nothing is, it
   * is a lie the screen keeps telling.
   *
   * So: an explicit status, and a failure that SAYS it failed and offers
   * the retry. That is #166/#217's rule -- a real HTTP error is the
   * server's answer and has to surface -- applied to a tab that predates
   * it.
   */
  const [status, setStatus] = useState("loading");
  const [lineage, setLineage] = useState([]);
  const [history, setHistory] = useState([]);
  const [performance, setPerformance] = useState([]);

  const [scoreForm, setScoreForm] = useState({ brood_pattern_score: 5, temperament_score: 5, notes: "" });
  const [addOpen, setAddOpen] = useState(false);
  const [queenForm, setQueenForm] = useState(EMPTY_QUEEN);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [replaceForm, setReplaceForm] = useState({
    ...EMPTY_QUEEN,
    reason: "",
    replaced_date: new Date().toISOString().slice(0, 10),
    notes: "",
    daughter_of_previous: false,
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const r = await hivesApi.queenLineage(hive.hive_id);
      setLineage(r.lineage || []);
      // `history` and `performance` are new in #259. An API that has not
      // been deployed yet answers without them, and that must not blank
      // the tab.
      setHistory(r.history || []);
      setPerformance(r.performance || []);
      setStatus("ready");
    } catch {
      setStatus("error");
    }
  }, [hive.hive_id]);

  useEffect(() => { load(); }, [load]);

  /**
   * The empty state named an action and offered no way to do it (#250).
   *
   * POST /queens has existed all along and already links the queen to the
   * hive when hive_id is passed -- create_queen sets Hives.current_queen_id
   * itself. So this was a missing form, not a missing capability, and the
   * screen sat there telling people to do something it would not let them
   * do.
   *
   * Every field except the hive is optional server-side, and stays optional
   * here: somebody hiving a swarm knows none of it, and refusing the record
   * until they do would lose the fact a queen exists at all.
   */
  async function addQueen() {
    setSaving(true);
    try {
      await queensApi.create({
        hive_id: hive.hive_id,
        source: queenForm.source.trim() || undefined,
        birth_year: queenForm.birth_year ? Number(queenForm.birth_year) : undefined,
        marking_color: queenForm.marking_color.trim() || undefined,
        genetic_line: queenForm.genetic_line.trim() || undefined,
      });
      notify("Queen added", "success");
      setAddOpen(false);
      setQueenForm(EMPTY_QUEEN);
      // The tab keys off hive.current_queen_id, which the server just set,
      // so the PAGE has to refetch -- refreshing only this tab would leave
      // it reading a stale hive and still showing the empty state.
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not add the queen", "danger");
    } finally {
      setSaving(false);
    }
  }

  /**
   * Requeening (#259).
   *
   * There was no way to do this at all: once a hive had a queen, the tab
   * offered lineage and a performance form for the rest of that hive's
   * life. A colony that supersedes twice in a season had one immortal
   * queen as far as the app was concerned.
   *
   * The reason is the only required field, because it is the whole value
   * of this record a year from now. Everything about the new queen stays
   * optional for the same reason it is optional in addQueen -- you may
   * have just dropped in a cell.
   */
  async function replaceQueen() {
    if (!replaceForm.reason) return notify("Pick what happened to the old queen", "warning");
    setSaving(true);
    try {
      await queensApi.replace({
        hive_id: hive.hive_id,
        reason: replaceForm.reason,
        replaced_date: replaceForm.replaced_date || undefined,
        notes: replaceForm.notes.trim() || undefined,
        daughter_of_previous: replaceForm.daughter_of_previous,
        source: replaceForm.source.trim() || undefined,
        birth_year: replaceForm.birth_year ? Number(replaceForm.birth_year) : undefined,
        marking_color: replaceForm.marking_color.trim() || undefined,
        genetic_line: replaceForm.genetic_line.trim() || undefined,
      });
      notify("Queen replaced", "success");
      setReplaceOpen(false);
      setReplaceForm((f) => ({ ...f, ...EMPTY_QUEEN, reason: "", notes: "", daughter_of_previous: false }));
      // Same reason as addQueen: the hive's pointer changed server-side.
      onChanged?.();
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not replace the queen", "danger");
    } finally {
      setSaving(false);
    }
  }

  const queenFields = (form, setForm) => (
    <div style={{ display: "grid", gap: "var(--space-3)" }}>
      <Field label="Where she came from">
        {(id) => <input id={id} className="touch-target" value={form.source}
          placeholder="Swarm, split, or a breeder's name"
          onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))} />}
      </Field>
      <Field label="Birth year">
        {(id) => <input id={id} type="number" className="touch-target" value={form.birth_year}
          onChange={(e) => setForm((f) => ({ ...f, birth_year: e.target.value }))} />}
      </Field>
      <Field label="Marking colour">
        {(id) => <input id={id} className="touch-target" value={form.marking_color}
          placeholder="White, yellow, red, green, blue"
          onChange={(e) => setForm((f) => ({ ...f, marking_color: e.target.value }))} />}
      </Field>
      <Field label="Genetic line">
        {(id) => <input id={id} className="touch-target" value={form.genetic_line}
          placeholder="Carniolan, Italian, Buckfast…"
          onChange={(e) => setForm((f) => ({ ...f, genetic_line: e.target.value }))} />}
      </Field>
    </div>
  );

  const addQueenModal = (
    <Modal open={addOpen} onClose={() => setAddOpen(false)} title="Add a queen"
      footer={<Button onClick={addQueen} disabled={saving}>{saving ? "Saving…" : "Add queen"}</Button>}>
      <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
        All of this is optional — a swarm you hived yesterday has no paperwork.
        Recording the queen at all is what starts her lineage.
      </p>
      {queenFields(queenForm, setQueenForm)}
    </Modal>
  );

  const historyCard = history.length > 0 ? (
    <Card title="Queen history" className="section-spacing">
      <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
        {history.map((h) => (
          <li key={h.replacement_id} style={{ marginBottom: "var(--space-2)" }}>
            <strong>{String(h.replaced_date).slice(0, 10)}</strong>
            {" — "}
            {REASONS.find((r) => r.value === h.reason)?.label || h.reason}
            <div style={{ color: "var(--color-text-muted)" }}>
              {h.old_queen_id
                ? `${describeQueen(h.old_marking_color, h.old_genetic_line, h.old_birth_year)} → ${describeQueen(h.new_marking_color, h.new_genetic_line, h.new_birth_year)}`
                : `First queen on record: ${describeQueen(h.new_marking_color, h.new_genetic_line, h.new_birth_year)}`}
            </div>
            {h.notes && <div style={{ color: "var(--color-text-muted)" }}>{h.notes}</div>}
          </li>
        ))}
      </ul>
    </Card>
  ) : null;

  const performanceCard = performance.length > 0 ? (
    <Card title="Performance" className="section-spacing">
            {/* One section per queen, not one list (#259).
                Flattened, a hive requeened mid-season shows a run of numbers
                that dip and recover, which reads as the colony improving.
                What actually happened is that a failing queen was replaced,
                and only the grouping says so. */}
            {performance.map((q) => (
              <section key={q.queen_id} style={{ marginBottom: "var(--space-4)" }}>
                <h4 style={{ margin: "0 0 var(--space-1)" }}>
                  {q.is_current ? "Current queen" : "Retired"} — {describeQueen(q.marking_color, q.genetic_line, q.birth_year)}
                  {!q.is_current && q.retired_date && (
                    <span style={{ fontWeight: 400, color: "var(--color-text-muted)" }}>
                      {" "}(replaced {String(q.retired_date).slice(0, 10)})
                    </span>
                  )}
                </h4>
                <ul style={{ margin: 0, paddingLeft: "1.2em" }}>
                  {q.logs.map((p) => (
                    <li key={p.log_id} style={{ marginBottom: "var(--space-1)" }}>
                      <strong>{String(p.date).slice(0, 10)}</strong>
                      {" — "}
                      brood {p.brood_pattern_score}/10, temperament {p.temperament_score}/10
                      {p.egg_laying_rate ? `, laying ${p.egg_laying_rate}` : ""}
                      {p.notes && (
                        <div style={{ color: "var(--color-text-muted)" }}>{p.notes}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </Card>
  ) : null;

  if (!hive.current_queen_id) {
    return (
      <>
        <EmptyState
          icon="👑"
          title="No queen on record"
          description="Add a queen to start tracking lineage and performance."
          action={<Button onClick={() => setAddOpen(true)}>Add a queen</Button>}
        />
        {/* A queenless hive that HAS been requeened before still has a past,
            and on this screen it is the most relevant thing there is --
            including what its previous queens actually did. */}
        {historyCard}
        {performanceCard}
        {addQueenModal}
      </>
    );
  }

  async function logPerformance() {
    try {
      await queensApi.logPerformance({
        queen_id: hive.current_queen_id,
        date: new Date().toISOString().slice(0, 10),
        brood_pattern_score: Number(scoreForm.brood_pattern_score),
        temperament_score: Number(scoreForm.temperament_score),
        notes: scoreForm.notes || undefined,
      });
      notify("Performance logged", "success");
      // Without this the score you just entered goes straight into a table
      // that will not show it until the page is reloaded -- which is how a
      // write-only feature feels even after it can be read.
      setScoreForm({ brood_pattern_score: 5, temperament_score: 5, notes: "" });
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log performance", "danger");
    }
  }

  return (
    <div>
      <Card
        title="Lineage"
        className="section-spacing"
        actions={<Button variant="secondary" onClick={() => setReplaceOpen(true)}>Replace queen</Button>}
      >
        {status === "loading" && <Spinner />}
        {status === "error" && (
          <div>
            <p style={{ marginTop: 0 }}>Couldn’t load this queen’s lineage.</p>
            <Button variant="secondary" onClick={load}>Try again</Button>
          </div>
        )}
        {status === "ready" && (
          lineage.length === 0 ? (
            <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
              Nothing recorded about where this queen came from.
            </p>
          ) : (
            <ul>
              {lineage.map((q) => (
                <li key={q.queen_id}>
                  Queen #{q.queen_id} — {q.marking_color || "unmarked"}, {q.genetic_line || "line unknown"} (depth {q.depth})
                </li>
              ))}
            </ul>
          )
        )}
      </Card>

      {historyCard}

      {performanceCard}

      <Card title="Log Performance">
        <div style={{ display: "grid", gap: "var(--space-3)", maxWidth: 360 }}>
          <label>
            Brood pattern (1-10)
            <input id="queen-tab-brood-pattern-score" name="brood-pattern-score" type="number" min={1} max={10} value={scoreForm.brood_pattern_score}
              onChange={(e) => setScoreForm((s) => ({ ...s, brood_pattern_score: e.target.value }))} className="touch-target" style={{ width: "100%" }} />
          </label>
          <label>
            Temperament (1-10)
            <input id="queen-tab-temperament-score" name="temperament-score" type="number" min={1} max={10} value={scoreForm.temperament_score}
              onChange={(e) => setScoreForm((s) => ({ ...s, temperament_score: e.target.value }))} className="touch-target" style={{ width: "100%" }} />
          </label>
          <label>
            Notes
            <textarea id="queen-tab-notes" name="notes" value={scoreForm.notes} onChange={(e) => setScoreForm((s) => ({ ...s, notes: e.target.value }))} rows={2} style={{ width: "100%" }} />
          </label>
          <Button onClick={logPerformance}>Save</Button>
        </div>
      </Card>

      <Modal open={replaceOpen} onClose={() => setReplaceOpen(false)} title="Replace this queen"
        footer={<Button onClick={replaceQueen} disabled={saving}>{saving ? "Saving…" : "Replace queen"}</Button>}>
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          The queen on record now is retired and kept — her performance logs
          and her lineage stay exactly where they are. Only what happened to
          her is required.
        </p>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="What happened to the old queen" required>
            {(id) => (
              <select id={id} className="touch-target" value={replaceForm.reason}
                onChange={(e) => setReplaceForm((f) => ({ ...f, reason: e.target.value }))}>
                <option value="">Choose…</option>
                {REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            )}
          </Field>
          <Field label="When">
            {(id) => <input id={id} type="date" className="touch-target" value={replaceForm.replaced_date}
              onChange={(e) => setReplaceForm((f) => ({ ...f, replaced_date: e.target.value }))} />}
          </Field>
          <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "flex-start" }}>
            <input id="queen-tab-daughter-of-previous" name="daughter-of-previous" type="checkbox" checked={replaceForm.daughter_of_previous}
              onChange={(e) => setReplaceForm((f) => ({ ...f, daughter_of_previous: e.target.checked }))} />
            {/* Ticked deliberately or not at all: the lineage walk treats
                mother_queen_id as fact, so a wrong tick is a wrong pedigree
                that nothing later can tell from a real one. */}
            <span>The new queen is the old queen’s daughter (supersedure or swarm cell)</span>
          </label>
          <Field label="Notes">
            {(id) => <textarea id={id} rows={2} value={replaceForm.notes}
              onChange={(e) => setReplaceForm((f) => ({ ...f, notes: e.target.value }))} />}
          </Field>
          <hr />
          <p style={{ margin: 0, color: "var(--color-text-muted)" }}>
            About the new queen — all optional.
          </p>
          {queenFields(replaceForm, setReplaceForm)}
        </div>
      </Modal>
    </div>
  );
}
